import { Dispatch, createContext, ReactNode, useContext, useEffect, FC } from 'react';
import noop from 'lodash/noop';
import { useImmerReducer } from 'use-immer';
import { FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import Cookie from 'js-cookie';

import { requestService } from 'shared/utils/services/requestService';
import { actions } from 'checkout/state/checkoutActions';
import { IAddress } from 'checkout/types/Address';
import { ITabName } from 'checkout/types/Tab';
import { useExpressPay } from 'checkout/hooks/useExpressPay';
import { useOrder, IPurchaseOrderProps } from 'checkout/hooks/useOrder';
import { usePayments } from 'checkout/hooks/usePayments';
import { useShippingAddresses } from 'checkout/hooks/useShippingAddresses';
import {
  checkoutReducer,
  ICheckoutState,
  INITIAL_CHECKOUT_STATE,
} from 'checkout/state/checkoutReducer';
import {
  IAction,
  IAddressesAction,
  IBillingInfoAction,
  IBillingInfosAction,
  IErrorAction,
  IGiftCardSuccessAction,
  IOrderSuccessAction,
  IProductTemplateSuccessAction,
  ITabChangeAction,
  IWantSuccessAction,
} from 'checkout/types/ActionPayload';
import { IKlarnaSessionRequestBody, IPayPalSessionRequestBody } from 'checkout/types/Payment';
import { IBillingInfo } from 'checkout/types/Billing';
import { PaymentType } from 'shared/enums/PaymentType';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { IOrder } from 'checkout/types/Order';
import { STORAGE } from 'shared/enums/SitePreferences';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';

interface ICheckoutContext extends ICheckoutState {
  addNewCreditCard: (address: IAddress, guestOrderNumber?: number) => Promise<void>;
  changeShippingServiceLevel: (shippingServiceLevel: string) => Promise<void>;
  changeTab: (tab: ITabName) => void;
  continueAsGuest: () => void;
  connectAddressToOrder: (addressId: number, newOrderNumber?: number) => Promise<void>;
  connectBillingInfoToOrder: (billingInfoId: number, manualOrderNumber?: number) => Promise<void>;
  createBillingAddress: (address: IAddress) => Promise<IAddress | void>;
  createKlarnaSession: (klarnaOptions: IKlarnaSessionRequestBody) => Promise<boolean>;
  createPayPalSession: (paypalOptions: IPayPalSessionRequestBody) => Promise<boolean>;
  createShippingAddress?: (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
    // TODO: change var name to guestOrderNumber?
    expressPayOrderNumber?: number,
  ) => Promise<IAddress | void>;
  createShippingAddressWithoutOrder?: (
    address: IAddress,
    formikActions?: FormikHelpers<IAddress>,
  ) => Promise<IAddress | void>;
  createStripeBillingInfo: (params: {
    billingAddressId?: number;
    name: string;
    stripeToken: string;
    paymentType: PaymentType;
  }) => Promise<IBillingInfo | void>;
  deleteAddress: (addressId: number) => Promise<void>;
  displayGlobalError: (message: string) => void;
  dismissExpressPayModal: () => void;
  dismissGlobalError: () => void;
  dismissInternationalFeeWarning: () => void;
  dispatch: Dispatch<IAction>;
  displayInternationalFeeWarning: boolean;
  ExpressPayButton?: FC<{ onTrackerClick?: () => void }>;
  redirectToAfterpay: () => Promise<boolean>;
  purchaseOrder: ({ paymentType }: IPurchaseOrderProps) => Promise<void>;
  updateOrder: ({ orderNumber, payload }: { orderNumber: number; payload: any }) => Promise<void>;
  createBillingInfo?: (billingInfo, paymentType: PaymentType) => Promise<IBillingInfo | null>;
  verifyCreditCard: () => Promise<void>;
  createGuestOrderRequestDispatcher: () => void;
  createGuestOrderFailureDispatcher: (message: string) => void;
  createGuestOrderSuccessDispatcher: (order: IOrder) => void;
  createStripeSource: (type: string, shippingAddress: IAddress) => Promise<void>;
}

interface ICheckoutContextProviderProps {
  children: ReactNode;
}

export const CheckoutContext = createContext<ICheckoutContext>({
  ...INITIAL_CHECKOUT_STATE,
  addNewCreditCard: () => Promise.resolve(),
  changeShippingServiceLevel: () => Promise.resolve(),
  changeTab: noop,
  continueAsGuest: noop,
  connectAddressToOrder: () => Promise.resolve(),
  connectBillingInfoToOrder: () => Promise.resolve(),
  createBillingAddress: () => Promise.resolve(),
  createKlarnaSession: () => Promise.resolve(false),
  createPayPalSession: () => Promise.resolve(false),
  createShippingAddress: () => Promise.resolve(),
  createShippingAddressWithoutOrder: () => Promise.resolve(),
  createStripeBillingInfo: () => Promise.resolve(),
  deleteAddress: () => Promise.resolve(),
  dispatch: noop,
  displayGlobalError: noop,
  dismissExpressPayModal: noop,
  dismissGlobalError: noop,
  dismissInternationalFeeWarning: noop,
  displayInternationalFeeWarning: false,
  purchaseOrder: () => Promise.resolve(),
  redirectToAfterpay: () => Promise.resolve(false),
  updateOrder: () => Promise.resolve(),
  verifyCreditCard: () => Promise.resolve(),
  createStripeSource: () => Promise.resolve(),
  createGuestOrderFailureDispatcher: noop,
  createGuestOrderRequestDispatcher: noop,
  createGuestOrderSuccessDispatcher: noop,
});

export const useCheckoutContext = () => useContext(CheckoutContext);

export const CheckoutContextProvider = ({ children }: ICheckoutContextProviderProps) => {
  const [state, dispatch] = useImmerReducer(checkoutReducer, INITIAL_CHECKOUT_STATE);
  const router = useRouter();

  const { isAuthenticated, user } = useStoreSelector((_state) => _state.auth);
  const {
    boxCondition,
    expectedPriceCents,
    giftCardId,
    instantShip,
    orderNumber,
    productId,
    productTemplateId,
    size,
  } = router?.query;

  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const isPaymentAvailabilityServiceEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_PAYMENT_AVAILABILITY_SERVICE,
  );
  const isTempWebEnableGuestCheckoutPriceBreakdown = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_GUEST_CHECKOUT_PRICE_BREAKDOWN,
  );
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout: user?.isGuest,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  const selectedCountryCode = Cookie.getJSON(STORAGE.COUNTRY);

  let orderAPIEndpoint = null;

  if (isAuthenticated && orderNumber) {
    orderAPIEndpoint = `/web-api/v1/orders/${orderNumber as string}`;
  }

  /**
   * TODO: once we remove guest checkout FF can move many of these calls
   * out of the provider and called server side which would also mean we can
   * remove some actions and loading states
   */
  const { data: order } = useSWR(
    orderAPIEndpoint,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchOrderFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IOrderSuccessAction>)({
          type: actions.fetchOrderSuccess,
          payload: data,
        });
      },
    },
  );

  /**
   * NOTE: when guest + unauthenticated and not a gift card purchase fetch guest
   * price estimate to check for express pay availability
   */
  useSWR(
    () =>
      isGuestCheckoutEnabled &&
      isTempWebEnableGuestCheckoutPriceBreakdown &&
      !isAuthenticated &&
      !isPurchasingGiftCard
        ? [
            '/web-api/v1/product_variants/price_breakdown',
            `?productTemplateId=${(productTemplateId as string) || ''}`,
            `&priceCents=${(expectedPriceCents as string) || ''}`,
            `&countryCode=${(selectedCountryCode as string) || ''}`,
            `&size=${(size as string) || ''}`,
            `&boxCondition=${(boxCondition as string) || ''}`,
            `&isInstantShip=${(instantShip as string) || ''}`,
            `&productId=${(productId as string) || ''}`,
          ].join('')
        : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchGuestPriceBreakdownFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IOrderSuccessAction>)({
          type: actions.fetchGuestPriceBreakdownSuccess,
          payload: data,
        });
      },
    },
  );

  /**
   * NOTE: for guest + unauthenticated and not a gift card purchases need to fetch
   * product information and add to order object
   */
  useSWR(
    () =>
      isGuestCheckoutEnabled &&
      !isAuthenticated &&
      !isPurchasingGiftCard &&
      Boolean(router?.query?.productTemplateId)
        ? `/web-api/v1/product_templates/${router?.query?.productTemplateId as string}/show_v2`
        : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchProductTemplateFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IProductTemplateSuccessAction>)({
          type: actions.fetchProductTemplateSuccess,
          payload: {
            productTemplate: data,
            queryParams: {
              boxCondition: router?.query?.boxCondition as string,
              productId: Number(router?.query?.productId) || null,
              productTemplateId: Number(router?.query?.productTemplateId) || null,
              shoeCondition: router?.query?.shoeCondition as string,
              size: router?.query?.size as string,
            },
          },
        });
      },
    },
  );

  /**
   * NOTE: for offers, PT data does not come back with the order object therefore
   * we need to make an extra call for the want and add to the order object
   */
  useSWR(
    () =>
      order?.wantId && isCheckoutOffersEnabled && isAuthenticated
        ? `/web-api/v1/wants/${order?.wantId as string}`
        : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchWantFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IWantSuccessAction>)({
          type: actions.fetchWantSuccess,
          payload: data,
        });
      },
    },
  );

  useSWR(
    isAuthenticated ? '/web-api/v1/addresses' : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchAddressesFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IAddressesAction>)({
          type: actions.fetchAddressesSuccess,
          payload: data?.addresses,
        });
      },
    },
  );

  useSWR(
    isAuthenticated ? '/web-api/v1/billing_infos' : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchBillingInfosFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IBillingInfosAction>)({
          type: actions.fetchBillingInfosSuccess,
          payload: data?.billingInfos,
        });
      },
    },
  );

  /**
   * NOTE: we make this extra call since billing infos that are not of payment type
   * credit card are not directly linked with the user thus must retrieve it from
   * the order. For paypal we intentionally do not save the billing info to avoid
   * fraud therefore will forgo this call as well
   */
  useSWR(
    () =>
      isAuthenticated &&
      order?.paymentType !== PaymentType.CreditCard &&
      order?.paymentType !== PaymentType.PayPal &&
      order?.billingInfoId
        ? `/web-api/v1/billing_infos/${order?.billingInfoId as number}`
        : null,
    (url) => requestService({ url, method: 'GET' }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        (dispatch as Dispatch<IErrorAction>)({
          type: actions.fetchBillingInfoFailure,
          payload: error.message,
        });
      },
      onSuccess: (data) => {
        (dispatch as Dispatch<IBillingInfoAction>)({
          type: actions.fetchBillingInfoSuccess,
          payload: data,
        });
      },
    },
  );

  const fetchGiftCard = async (id = '') => {
    dispatch({ type: actions.fetchGiftCardRequest });

    try {
      const giftCard = await requestService({
        url: `/web-api/v1/gift-cards/${id}`,
        method: 'get',
      });

      const space = await requestService({
        url: `/web-api/v1/spaces/${giftCard.spaceId as string}`,
        method: 'get',
      });

      (dispatch as Dispatch<IGiftCardSuccessAction>)({
        type: actions.fetchGiftCardSuccess,
        payload: { ...giftCard, space: space.data },
      });
    } catch (error) {
      (dispatch as Dispatch<IErrorAction>)({
        type: actions.fetchGiftCardFailure,
        payload: error.message,
      });
    }
  };

  const { ExpressPayButton } = useExpressPay({
    addresses: state.addresses,
    dispatch,
    order: state.order,
  });

  const { purchaseOrder, updateOrder } = useOrder({ dispatch, order: state.order });

  const {
    addNewCreditCard,
    connectBillingInfoToOrder,
    createBillingAddress,
    createBillingInfo,
    createKlarnaSession,
    createPayPalSession,
    createStripeBillingInfo,
    createStripeSource,
    listAvailablePaymentMethods,
    redirectToAfterpay,
    verifyCreditCard,
  } = usePayments({
    dispatch,
    order: state.order,
  });

  const {
    changeShippingServiceLevel,
    connectAddressToOrder,
    createShippingAddress,
    createShippingAddressWithoutOrder,
    deleteAddress,
  } = useShippingAddresses({ dispatch, order: state.order, addresses: state.addresses });

  /**
   * *************************
   * User action dispatchers
   * *************************
   */
  const continueAsGuest = () => dispatch({ type: actions.continueAsGuest });

  /**
   * *************************
   * Guest order action dispatchers
   * *************************
   */
  const createGuestOrderFailureDispatcher = (message: string): void =>
    (dispatch as Dispatch<IErrorAction>)({
      type: actions.createGuestOrderFailure,
      payload: message,
    });

  const createGuestOrderRequestDispatcher = (): void =>
    dispatch({
      type: actions.createGuestOrderRequest,
    });

  const createGuestOrderSuccessDispatcher = (guestOrder: IOrder): void =>
    (dispatch as Dispatch<IOrderSuccessAction>)({
      type: actions.createGuestOrderSuccess,
      payload: guestOrder,
    });

  /**
   * *************************
   * Tab action dispatchers
   * *************************
   */
  const changeTab = (tab: ITabName): void =>
    (dispatch as Dispatch<ITabChangeAction>)({ type: actions.changeTab, payload: tab });

  /**
   * *************************
   * Error dispatchers
   * *************************
   */
  const displayGlobalError = (message: string) =>
    (dispatch as Dispatch<IErrorAction>)({ type: actions.displayGlobalError, payload: message });
  const dismissGlobalError = () => dispatch({ type: actions.dismissGlobalError });
  const dismissInternationalFeeWarning = () =>
    dispatch({ type: actions.dismissInternationalFeeWarning });

  /**
   * *************************
   * Express Pay dispatchers
   * *************************
   */
  const dismissExpressPayModal = () => dispatch({ type: actions.dismissExpressPayModal });

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info('checkout state:', state);
    }
  }, [state]);

  /**
   * *************************
   * Payment Webhooks errors
   * *************************
   */
  useEffect(() => {
    // These errors are set in the payment webhooks before redirecting to checkout.
    // The redirection would wipe out the error message, so we need to store it in a cookie.
    // Errors such as "Payment failed" or "Product no longer available"
    const checkoutError = Cookie.getJSON(STORAGE.CHECKOUT_ERROR);
    if (typeof checkoutError?.messages?.[0] === 'string') {
      displayGlobalError(checkoutError.messages[0]);
    }
    Cookie.remove(STORAGE.CHECKOUT_ERROR, { path: '/' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isPaymentAvailabilityServiceEnabled &&
      state.order?.localizedFinalPriceCents?.amount >= 0 &&
      !state.loading.availablePaymentMethods
    ) {
      void listAvailablePaymentMethods({
        shippingRegion: state.addresses?.[state.order?.addressId]?.countryCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPaymentAvailabilityServiceEnabled,
    state.order?.localizedFinalPriceCents?.amount,
    state.order?.localizedFinalPriceCents?.currency,
    state.order?.addressId,
  ]);

  useEffect(() => {
    if (
      (!!order?.product?.giftCardId || isGuestPurchasingGiftCard) &&
      isCheckoutPurchaseGiftCardsEnabled
    ) {
      const id = order?.product?.giftCardId || router?.query?.giftCardId;

      void fetchGiftCard(id);
    }
  }, [order?.product?.giftCardId]);

  useEffect(() => {
    if (user?.isGuest) {
      continueAsGuest();
    }
  }, [user?.isGuest]);

  const contextValues = {
    ...state,
    ExpressPayButton,
    addNewCreditCard,
    changeShippingServiceLevel,
    changeTab,
    connectAddressToOrder,
    connectBillingInfoToOrder,
    continueAsGuest,
    createBillingAddress,
    createBillingInfo,
    createGuestOrderFailureDispatcher,
    createGuestOrderRequestDispatcher,
    createGuestOrderSuccessDispatcher,
    createKlarnaSession,
    createPayPalSession,
    createShippingAddress,
    createShippingAddressWithoutOrder,
    createStripeBillingInfo,
    createStripeSource,
    deleteAddress,
    dispatch,
    dismissExpressPayModal,
    dismissGlobalError,
    dismissInternationalFeeWarning,
    displayGlobalError,
    purchaseOrder,
    redirectToAfterpay,
    updateOrder,
    verifyCreditCard,
  };

  return <CheckoutContext.Provider value={contextValues}>{children}</CheckoutContext.Provider>;
};
